/**
 * Abstract supply-chain network visual built as inline SVG.
 * Faint lane lines, shipment nodes, signal pulses, cost-leakage traces.
 */
export default function NetworkVisual({ className = "" }) {
  return (
    <div className={`relative w-full h-full ${className}`} data-testid="hero-network-visual">
      <svg
        viewBox="0 0 600 600"
        className="w-full h-full"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <radialGradient id="bgGlow" cx="50%" cy="45%" r="55%">
            <stop offset="0%" stopColor="#3B82F6" stopOpacity="0.18" />
            <stop offset="100%" stopColor="#3B82F6" stopOpacity="0" />
          </radialGradient>
          <linearGradient id="lane" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#3B82F6" stopOpacity="0" />
            <stop offset="50%" stopColor="#3B82F6" stopOpacity="0.55" />
            <stop offset="100%" stopColor="#3B82F6" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="laneFaint" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#94A3B8" stopOpacity="0" />
            <stop offset="50%" stopColor="#94A3B8" stopOpacity="0.32" />
            <stop offset="100%" stopColor="#94A3B8" stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* Background glow */}
        <circle cx="300" cy="280" r="260" fill="url(#bgGlow)" />

        {/* Concentric range rings */}
        {[80, 140, 210, 280].map((r, i) => (
          <circle
            key={r}
            cx="300"
            cy="300"
            r={r}
            stroke="rgba(255,255,255,0.05)"
            strokeWidth="1"
            strokeDasharray={i % 2 === 0 ? "1 6" : "2 4"}
          />
        ))}

        {/* Grid lines */}
        {[0, 1, 2, 3, 4].map((i) => (
          <line
            key={`v${i}`}
            x1={150 + i * 75}
            y1="60"
            x2={150 + i * 75}
            y2="540"
            stroke="rgba(255,255,255,0.04)"
            strokeWidth="1"
          />
        ))}
        {[0, 1, 2, 3, 4].map((i) => (
          <line
            key={`h${i}`}
            x1="60"
            y1={150 + i * 75}
            x2="540"
            y2={150 + i * 75}
            stroke="rgba(255,255,255,0.04)"
            strokeWidth="1"
          />
        ))}

        {/* Lane traces */}
        <path d="M80 200 Q 200 140, 300 220 T 540 180" stroke="url(#laneFaint)" strokeWidth="1.25" />
        <path d="M70 380 Q 220 320, 320 420 T 530 360" stroke="url(#laneFaint)" strokeWidth="1.25" />
        <path d="M120 110 Q 280 260, 480 120" stroke="url(#lane)" strokeWidth="1.5" />
        <path d="M120 500 Q 280 360, 480 510" stroke="url(#lane)" strokeWidth="1.5" />
        <path d="M300 60 L 300 540" stroke="rgba(59,130,246,0.18)" strokeDasharray="2 6" />

        {/* Nodes */}
        {[
          { x: 120, y: 110, r: 4, active: true },
          { x: 480, y: 120, r: 4, active: true },
          { x: 120, y: 500, r: 4, active: false },
          { x: 480, y: 510, r: 4, active: true },
          { x: 220, y: 280, r: 3, active: false },
          { x: 380, y: 240, r: 3, active: true },
          { x: 300, y: 300, r: 6, active: true },
          { x: 360, y: 410, r: 3, active: false },
          { x: 200, y: 380, r: 3, active: true },
        ].map((n, i) => (
          <g key={i}>
            <circle
              cx={n.x}
              cy={n.y}
              r={n.r + 6}
              fill={n.active ? "rgba(59,130,246,0.18)" : "rgba(148,163,184,0.08)"}
            />
            <circle
              cx={n.x}
              cy={n.y}
              r={n.r}
              fill={n.active ? "#3B82F6" : "#94A3B8"}
            />
          </g>
        ))}

        {/* Central crosshair label */}
        <g>
          <line x1="270" y1="300" x2="330" y2="300" stroke="rgba(59,130,246,0.6)" strokeWidth="1" />
          <line x1="300" y1="270" x2="300" y2="330" stroke="rgba(59,130,246,0.6)" strokeWidth="1" />
          <circle cx="300" cy="300" r="14" stroke="rgba(59,130,246,0.5)" strokeWidth="1" fill="none" />
        </g>

        {/* Floating data labels */}
        <g fontFamily="JetBrains Mono, monospace" fontSize="9" fill="#94A3B8">
          <text x="60" y="80">N-01 / ORIGIN</text>
          <text x="430" y="100">N-07 / DC-WEST</text>
          <text x="60" y="540">N-12 / VENDOR</text>
          <text x="420" y="540">N-19 / DEST</text>
          <text x="310" y="295">SIGNAL.42</text>
        </g>

        {/* Cost-leakage signal trace (animated) */}
        <g>
          <line
            x1="80"
            y1="450"
            x2="540"
            y2="450"
            stroke="rgba(255,255,255,0.05)"
            strokeWidth="1"
          />
          <circle cx="0" cy="450" r="3" fill="#3B82F6">
            <animate
              attributeName="cx"
              from="80"
              to="540"
              dur="6s"
              repeatCount="indefinite"
            />
            <animate
              attributeName="opacity"
              values="0;1;1;0"
              dur="6s"
              repeatCount="indefinite"
            />
          </circle>
        </g>
      </svg>

      {/* Corner brackets */}
      <div className="absolute top-0 left-0 w-6 h-6 border-l border-t border-white/15" />
      <div className="absolute top-0 right-0 w-6 h-6 border-r border-t border-white/15" />
      <div className="absolute bottom-0 left-0 w-6 h-6 border-l border-b border-white/15" />
      <div className="absolute bottom-0 right-0 w-6 h-6 border-r border-b border-white/15" />
    </div>
  );
}
